import JSZip from 'jszip';
import { supabase } from '@/lib/supabase';
import { nowMexicoISO } from '@/lib/dates';

// Tamaño de página para el barrido completo. Se recorre por bloques con
// `.range()` hasta agotar los registros, así evitamos que el límite por
// defecto de la API (1000 filas) deje información fuera del respaldo.
const PAGE_SIZE = 1000;

export interface BackupTableConfig {
  table: string;
  filename: string;
  label: string;
}

export const BACKUP_TABLES: BackupTableConfig[] = [
  { table: 'erp_clients', filename: 'clientes', label: 'Clientes' },
  { table: 'erp_subscriptions', filename: 'suscripciones', label: 'Suscripciones' },
  { table: 'erp_products', filename: 'productos', label: 'Catálogo de productos' },
  { table: 'erp_invoices', filename: 'facturas', label: 'Facturas' },
  { table: 'erp_contacts', filename: 'contactos', label: 'Contactos' },
  { table: 'erp_payments', filename: 'pagos', label: 'Historial de Pagos' },
];

type Row = Record<string, unknown>;

export interface BackupProgress {
  message: string;
  percent: number;
}

export interface BackupResult {
  filename: string;
  recordCounts: Record<string, number>;
  failedTables: string[];
  createdAt: string;
}

// Descarga completa de una tabla, paginando hasta agotar los registros reales.
async function fetchAllRows(table: string): Promise<Row[]> {
  const rows: Row[] = [];
  let from = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .range(from, to);

    if (error) throw new Error(error.message);

    const batch = (data || []) as Row[];
    rows.push(...batch);

    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows;
}

// Convierte los valores anidados (objetos, arreglos como `pending_additions`)
// en texto JSON para que la hoja de cálculo no pierda información.
function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    const serialized = JSON.stringify(value).replace(/"/g, '""');
    return `"${serialized}"`;
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const text = String(value).replace(/"/g, '""');
  return `"${text}"`;
}

function toCsv(rows: Row[]): string {
  const headerSet: string[] = [];
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!headerSet.includes(key)) headerSet.push(key);
    });
  });

  if (headerSet.length === 0) return '\uFEFF';

  const headerLine = headerSet.map((key) => `"${key}"`).join(',');
  const dataLines = rows.map((row) => headerSet.map((key) => csvCell(row[key])).join(','));

  return '\uFEFF' + [headerLine, ...dataLines].join('\r\n');
}

// Nombre de archivo con la fecha/hora local de la Ciudad de México.
function buildFileName(createdAt: string): string {
  const [datePart, timePart = '00:00:00'] = createdAt.split('T');
  const date = (datePart || '').replace(/-/g, '');
  const time = timePart.replace(/:/g, '');
  return `backup_telemetry_erp_${date}_${time}.zip`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Genera una copia de seguridad completa del ERP y dispara su descarga
 * en formato ZIP. Incluye un JSON estructurado y un CSV por cada tabla,
 * más un `metadata.json` con el conteo de registros, la versión de la app
 * y el timestamp en hora de la Ciudad de México.
 */
export async function exportDatabaseBackup(
  onProgress?: (progress: BackupProgress) => void,
): Promise<BackupResult> {
  const zip = new JSZip();
  const dataFolder = zip.folder('datos');
  const recordCounts: Record<string, number> = {};
  const failedTables: string[] = [];

  const totalTables = BACKUP_TABLES.length;
  const basePercent = 5;
  const spanPercent = 75;

  for (let index = 0; index < totalTables; index += 1) {
    const config = BACKUP_TABLES[index];
    const percent = Math.round(basePercent + (index / totalTables) * spanPercent);

    onProgress?.({
      message: `Consultando ${config.label.toLowerCase()}…`,
      percent,
    });

    let rows: Row[] = [];
    try {
      rows = await fetchAllRows(config.table);
    } catch {
      failedTables.push(config.label);
    }

    recordCounts[config.table] = rows.length;

    dataFolder?.file(`${config.filename}.json`, JSON.stringify(rows, null, 2));
    dataFolder?.file(`${config.filename}.csv`, toCsv(rows));
  }

  onProgress?.({ message: 'Empaquetando archivo ZIP…', percent: 85 });

  const createdAt = nowMexicoISO();
  const metadata = {
    app_version: 'Telemetry ERP v1.0',
    created_at: createdAt,
    timezone: 'America/Mexico_City',
    record_counts: recordCounts,
    tables: BACKUP_TABLES.map((config) => ({
      table: config.table,
      file: `datos/${config.filename}`,
    })),
    failed_tables: failedTables,
  };

  zip.file('metadata.json', JSON.stringify(metadata, null, 2));
  zip.file(
    'LEEME.txt',
    [
      'Respaldo completo — Telemetry ERP',
      '',
      `Generado: ${createdAt} (hora de la Ciudad de México)`,
      '',
      'Contenido:',
      '- datos/*.json  → registros completos con tipos de datos, arreglos y objetos.',
      '- datos/*.csv   → misma información lista para abrir en hoja de cálculo.',
      '- metadata.json → conteo de registros por tabla y versión de la aplicación.',
    ].join('\n'),
  );

  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });

  onProgress?.({ message: 'Descargando respaldo…', percent: 96 });

  const filename = buildFileName(createdAt);
  triggerDownload(blob, filename);

  onProgress?.({ message: 'Respaldo completado', percent: 100 });

  return { filename, recordCounts, failedTables, createdAt };
}